import { useEffect, useRef, useState } from "react";
import type { LivestreamRandomParticipantDraw } from "./livestreamRandomParticipant";

const ANIMATION_DURATION_MS = 7000;
const ITEM_HEIGHT_PX = 56;
const HOLD_AFTER_SETTLE_MS = 4000;

function getAnimationProgress({ elapsedMs }: { elapsedMs: number }) {
  const t = Math.min(elapsedMs / ANIMATION_DURATION_MS, 1);
  return 1 - (1 - t) ** 3.6;
}

function buildReelSequence({
  names,
  winner,
}: {
  names: string[];
  winner: string;
}) {
  const pool = names.length > 0 ? names : [winner];
  const sequence: string[] = [];

  for (let index = 0; index < 48; index += 1) {
    sequence.push(pool[index % pool.length]!);
  }

  sequence[sequence.length - 1] = winner;
  return sequence;
}

export function LivestreamRandomParticipantOverlay({
  draw,
  onComplete,
}: {
  draw: LivestreamRandomParticipantDraw | null;
  onComplete: () => void;
}) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState(0);
  const [settled, setSettled] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const sequenceRef = useRef<string[]>([]);
  const reelRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const holdTimeoutRef = useRef<number | null>(null);
  const fadeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!draw) {
      setVisible(false);
      setSettled(false);
      setFadingOut(false);
      return;
    }

    sequenceRef.current = buildReelSequence({
      names: draw.names,
      winner: draw.winner,
    });
    setVisible(true);
    setSettled(false);
    setFadingOut(false);
    positionRef.current = 0;
    setPosition(0);
    if (reelRef.current) {
      reelRef.current.style.transform = `translateY(${ITEM_HEIGHT_PX}px)`;
    }

    const start = performance.now();
    const maxPosition = sequenceRef.current.length - 1;

    const applyPosition = (nextPosition: number) => {
      positionRef.current = nextPosition;
      if (reelRef.current) {
        reelRef.current.style.transform = `translateY(${(1 - nextPosition) * ITEM_HEIGHT_PX}px)`;
      }
      setPosition(nextPosition);
    };

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = getAnimationProgress({ elapsedMs: elapsed });
      applyPosition(progress * maxPosition);

      if (elapsed < ANIMATION_DURATION_MS) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      applyPosition(maxPosition);
      setSettled(true);

      holdTimeoutRef.current = window.setTimeout(() => {
        setFadingOut(true);
        fadeTimeoutRef.current = window.setTimeout(() => {
          setVisible(false);
          onComplete();
        }, 500);
      }, HOLD_AFTER_SETTLE_MS);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (holdTimeoutRef.current != null) window.clearTimeout(holdTimeoutRef.current);
      if (fadeTimeoutRef.current != null) window.clearTimeout(fadeTimeoutRef.current);
    };
  }, [draw?.id, onComplete]);

  if (!visible || !draw) return null;

  const centerIndex = Math.min(
    sequenceRef.current.length - 1,
    Math.max(0, Math.round(position)),
  );
  const center = sequenceRef.current[centerIndex] ?? draw.winner;

  return (
    <div
      className={`pointer-events-none absolute inset-0 z-40 flex items-center justify-center transition-opacity duration-500 ${
        fadingOut ? "opacity-0" : "opacity-100"
      }`}
      aria-live="polite"
      aria-label={settled ? `Selected participant: ${draw.winner}` : "Selecting random participant"}
    >
      <div className="rounded-2xl border border-white/15 bg-black/70 px-8 py-6 shadow-[0_8px_40px_rgba(0,0,0,0.55)] backdrop-blur-md">
        <p className="mb-3 text-center text-xs font-medium uppercase tracking-[0.2em] text-white/50">
          {settled ? "Selected" : "Selecting"}
        </p>
        <div
          className="relative overflow-hidden"
          style={{ height: ITEM_HEIGHT_PX * 3, width: "min(80vw, 20rem)" }}
        >
          <div
            className="absolute inset-x-0 top-1/2 z-10 -translate-y-1/2 rounded-lg border border-white/20 bg-white/5"
            style={{ height: ITEM_HEIGHT_PX }}
          />
          <div ref={reelRef} className="absolute inset-x-0 top-0 will-change-transform">
            {sequenceRef.current.map((name, index) => {
              const distanceFromCenter = Math.abs(index - position);
              const centerBlend = Math.max(0, 1 - distanceFromCenter);
              const adjacentBlend = Math.max(0, 1 - Math.abs(distanceFromCenter - 1));

              return (
                <div
                  key={`${draw.id}-${index}`}
                  className="flex items-center justify-center px-2 text-center font-medium"
                  style={{
                    height: ITEM_HEIGHT_PX,
                    fontSize: `${1 + centerBlend * 0.5}rem`,
                    fontWeight: centerBlend > 0.5 ? 700 : 500,
                    color: `rgba(255, 255, 255, ${0.1 + centerBlend * 0.9 + adjacentBlend * 0.25})`,
                  }}
                >
                  <span className="truncate">{name}</span>
                </div>
              );
            })}
          </div>
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/80 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent" />
        </div>
        {settled ? (
          <p className="mt-4 text-center text-sm text-white/60">
            <span className="font-semibold text-white">{center}</span>
          </p>
        ) : (
          <div className="mt-4 flex justify-center gap-1" aria-hidden>
            <span className="h-1 w-1 animate-pulse rounded-full bg-white/40" />
            <span className="h-1 w-1 animate-pulse rounded-full bg-white/40 [animation-delay:120ms]" />
            <span className="h-1 w-1 animate-pulse rounded-full bg-white/40 [animation-delay:240ms]" />
          </div>
        )}
      </div>
    </div>
  );
}
